import { readErrorMessage } from '@Components/util/utils'
import { FraudServiceAPI } from '@API/data-services/fraud-service'
import {
  AuthScopes,
  FraudControlConfig,
  InvalidCountRule,
  PenaltyDuration,
  PenaltyTier,
} from '@Generated/back-office-client/src'
import { RootState } from '@AppState/redux-store'
import { TrashIcon } from '@radix-ui/react-icons'
import {
  Button,
  Card,
  Container,
  Flex,
  Heading,
  IconButton,
  Section,
  Select,
  Skeleton,
  Switch,
  Text,
  TextField,
} from '@radix-ui/themes'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { v4 as uuidv4, v5 as uuidv5 } from 'uuid'

type DurationUnit = 'minutes' | 'hours' | 'days' | 'weeks'

const DURATION_UNITS: DurationUnit[] = ['minutes', 'hours', 'days', 'weeks']

type DurationForm = {
  indefinite: boolean
  value: string
  unit: DurationUnit
}

type VelocityForm = {
  enabled: boolean
  id: string
  displayName: string
  maxScans: string
  windowSeconds: string
}

type InvalidRuleForm = {
  id: string
  displayName: string
  threshold: string
  windowValue: string
  windowUnit: DurationUnit
}

type TierForm = {
  key: string
  lockout: DurationForm
  probation: DurationForm
}

const defaultDuration = (): DurationForm => ({ indefinite: false, value: '24', unit: 'hours' })

/**
 * Deterministic per-customer id so the velocity rule keeps the same identity across
 * disable/re-enable cycles — restriction rows reference it and the fraud engine keys its
 * Redis rolling window on it.
 */
const velocityRuleId = (customerId: string | null | undefined): string => {
  if (customerId) {
    return uuidv5('velocity-rule', customerId)
  }
  return uuidv4()
}

const toDurationForm = (duration: PenaltyDuration | null): DurationForm => {
  if (!duration) {
    return { indefinite: true, value: '', unit: 'hours' }
  }
  for (const unit of DURATION_UNITS) {
    const value = duration[unit]
    if (value) {
      return { indefinite: false, value: String(value), unit }
    }
  }
  return { indefinite: false, value: '', unit: 'hours' }
}

const fromDurationForm = (form: DurationForm): PenaltyDuration | null => {
  if (form.indefinite) {
    return null
  }
  return { [form.unit]: Number(form.value) }
}

const toWindowForm = (
  rule: InvalidCountRule,
): { windowValue: string; windowUnit: DurationUnit } => {
  for (const unit of DURATION_UNITS) {
    const value = rule.windowDuration[unit]
    if (value) {
      return { windowValue: String(value), windowUnit: unit }
    }
  }
  return { windowValue: '', windowUnit: 'hours' }
}

export const FraudConfig = () => {
  const queryClient = useQueryClient()
  const { activeCustomerId, currentUser } = useSelector((state: RootState) => state.users)
  const canWrite = !!currentUser?.scopes.includes(AuthScopes.writefraud_controls)
  const queryKey = ['fraud-config', activeCustomerId]

  const [velocity, setVelocity] = useState<VelocityForm>({
    enabled: false,
    id: velocityRuleId(activeCustomerId),
    displayName: '',
    maxScans: '',
    windowSeconds: '',
  })
  const [invalidRules, setInvalidRules] = useState<InvalidRuleForm[]>([])
  const [tiers, setTiers] = useState<TierForm[]>([])
  const [alertEmails, setAlertEmails] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => FraudServiceAPI.getConfig(),
  })

  useEffect(() => {
    const config = data?.config
    if (!config) {
      return
    }

    if (config.velocityRule) {
      setVelocity({
        enabled: true,
        id: config.velocityRule.id,
        displayName: config.velocityRule.displayName,
        maxScans: String(config.velocityRule.maxScans),
        windowSeconds: String(config.velocityRule.windowSeconds),
      })
    }
    setInvalidRules(
      config.invalidCountRules.map((rule) => ({
        id: rule.id,
        displayName: rule.displayName,
        threshold: String(rule.threshold),
        ...toWindowForm(rule),
      })),
    )
    setTiers(
      config.penaltyTiers.map((tier: PenaltyTier) => ({
        key: uuidv4(),
        lockout: toDurationForm(tier.lockoutDuration),
        probation: toDurationForm(tier.probationDuration),
      })),
    )
    setAlertEmails(config.alertEmailDistribution.join(', '))
  }, [data])

  const buildConfig = (): FraudControlConfig => {
    const config: FraudControlConfig = {
      invalidCountRules: invalidRules.map((rule) => ({
        id: rule.id,
        displayName: rule.displayName,
        threshold: Number(rule.threshold),
        windowDuration: { [rule.windowUnit]: Number(rule.windowValue) },
      })),
      penaltyTiers: tiers.map((tier) => ({
        lockoutDuration: fromDurationForm(tier.lockout),
        probationDuration: fromDurationForm(tier.probation),
      })),
      alertEmailDistribution: alertEmails
        .split(',')
        .map((email) => email.trim())
        .filter((email) => email.length > 0),
    }

    if (velocity.enabled) {
      config.velocityRule = {
        id: velocity.id,
        displayName: velocity.displayName,
        maxScans: Number(velocity.maxScans),
        windowSeconds: Number(velocity.windowSeconds),
      }
    } else {
      config.velocityRule = null
    }

    return config
  }

  const { mutate: saveConfig, isPending: isSaving } = useMutation({
    mutationFn: () => FraudServiceAPI.updateConfig(buildConfig()),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKey, { config: updated })
      setSubmitError(null)
      setSaved(true)
    },
    onError: async (error) => {
      setSaved(false)
      setSubmitError(await readErrorMessage(error, 'Failed to save fraud config'))
    },
  })

  const updateVelocity = (updates: Partial<VelocityForm>) => {
    setVelocity({ ...velocity, ...updates })
    setSaved(false)
  }

  const updateInvalidRules = (rules: InvalidRuleForm[]) => {
    setInvalidRules(rules)
    setSaved(false)
  }

  const updateInvalidRule = (index: number, updates: Partial<InvalidRuleForm>) => {
    updateInvalidRules(
      invalidRules.map((rule, i) => (i === index ? { ...rule, ...updates } : rule)),
    )
  }

  const updateTiers = (newTiers: TierForm[]) => {
    setTiers(newTiers)
    setSaved(false)
  }

  const updateTier = (index: number, updates: Partial<TierForm>) => {
    updateTiers(tiers.map((tier, i) => (i === index ? { ...tier, ...updates } : tier)))
  }

  const updateAlertEmails = (value: string) => {
    setAlertEmails(value)
    setSaved(false)
  }

  const durationFields = (
    duration: DurationForm,
    onChange: (updates: Partial<DurationForm>) => void,
  ) => {
    return (
      <Flex gap="2" align="center">
        <Flex gap="1" align="center">
          <Switch
            size="1"
            checked={duration.indefinite}
            disabled={!canWrite}
            onCheckedChange={(checked) => onChange({ indefinite: checked === true })}
          />
          <Text size="1">Indefinite</Text>
        </Flex>
        {duration.indefinite ? null : (
          <>
            <TextField.Root
              size="1"
              type="number"
              style={{ width: 70 }}
              value={duration.value}
              disabled={!canWrite}
              onChange={(e) => onChange({ value: e.target.value })}
            />
            <Select.Root
              size="1"
              value={duration.unit}
              disabled={!canWrite}
              onValueChange={(unit) => onChange({ unit: unit as DurationUnit })}>
              <Select.Trigger />
              <Select.Content>
                {DURATION_UNITS.map((unit) => (
                  <Select.Item key={unit} value={unit}>
                    {unit}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </>
        )}
      </Flex>
    )
  }

  return (
    <Container>
      <Section>
        <Flex direction="column" gap="4">
          <Heading as="h1" size="5">
            Ticket Scan Fraud Config
          </Heading>

          <Skeleton loading={isLoading}>
            <Flex direction="column" gap="4">
              <Card>
                <Flex direction="column" gap="3">
                  <Flex justify="between" align="center">
                    <Heading as="h2" size="3">
                      Velocity Rule
                    </Heading>
                    <Flex gap="2" align="center">
                      <Switch
                        checked={velocity.enabled}
                        disabled={!canWrite}
                        onCheckedChange={(checked) => updateVelocity({ enabled: checked === true })}
                      />
                      <Text size="2">Enabled</Text>
                    </Flex>
                  </Flex>
                  <Text size="2" color="gray">
                    Blocks a player who scans more than the allowed number of tickets within the
                    window.
                  </Text>
                  {velocity.enabled ? (
                    <Flex gap="3" wrap="wrap">
                      <Flex direction="column" gap="1">
                        <Text size="2" weight="medium">
                          Display Name
                        </Text>
                        <TextField.Root
                          value={velocity.displayName}
                          disabled={!canWrite}
                          onChange={(e) => updateVelocity({ displayName: e.target.value })}
                        />
                      </Flex>
                      <Flex direction="column" gap="1">
                        <Text size="2" weight="medium">
                          Max Scans
                        </Text>
                        <TextField.Root
                          type="number"
                          value={velocity.maxScans}
                          disabled={!canWrite}
                          onChange={(e) => updateVelocity({ maxScans: e.target.value })}
                        />
                      </Flex>
                      <Flex direction="column" gap="1">
                        <Text size="2" weight="medium">
                          Window (seconds, max 60)
                        </Text>
                        <TextField.Root
                          type="number"
                          value={velocity.windowSeconds}
                          disabled={!canWrite}
                          onChange={(e) => updateVelocity({ windowSeconds: e.target.value })}
                        />
                      </Flex>
                    </Flex>
                  ) : null}
                </Flex>
              </Card>

              <Card>
                <Flex direction="column" gap="3">
                  <Flex justify="between" align="center">
                    <Heading as="h2" size="3">
                      Invalid Scan Count Rules
                    </Heading>
                    {canWrite ? (
                      <Button
                        variant="soft"
                        onClick={() =>
                          updateInvalidRules([
                            ...invalidRules,
                            {
                              id: uuidv4(),
                              displayName: '',
                              threshold: '',
                              windowValue: '1',
                              windowUnit: 'hours',
                            },
                          ])
                        }>
                        Add rule
                      </Button>
                    ) : null}
                  </Flex>
                  <Text size="2" color="gray">
                    Blocks a player who accumulates the threshold number of suspicious scans within
                    the rolling window.
                  </Text>
                  {invalidRules.length === 0 ? <Text size="2">No rules configured.</Text> : null}
                  {invalidRules.map((rule, index) => (
                    <Flex key={rule.id} gap="3" align="end" wrap="wrap">
                      <Flex direction="column" gap="1">
                        <Text size="2" weight="medium">
                          Display Name
                        </Text>
                        <TextField.Root
                          value={rule.displayName}
                          disabled={!canWrite}
                          onChange={(e) =>
                            updateInvalidRule(index, { displayName: e.target.value })
                          }
                        />
                      </Flex>
                      <Flex direction="column" gap="1">
                        <Text size="2" weight="medium">
                          Threshold
                        </Text>
                        <TextField.Root
                          type="number"
                          style={{ width: 90 }}
                          value={rule.threshold}
                          disabled={!canWrite}
                          onChange={(e) => updateInvalidRule(index, { threshold: e.target.value })}
                        />
                      </Flex>
                      <Flex direction="column" gap="1">
                        <Text size="2" weight="medium">
                          Window
                        </Text>
                        <Flex gap="2">
                          <TextField.Root
                            type="number"
                            style={{ width: 90 }}
                            value={rule.windowValue}
                            disabled={!canWrite}
                            onChange={(e) =>
                              updateInvalidRule(index, { windowValue: e.target.value })
                            }
                          />
                          <Select.Root
                            value={rule.windowUnit}
                            disabled={!canWrite}
                            onValueChange={(unit) =>
                              updateInvalidRule(index, { windowUnit: unit as DurationUnit })
                            }>
                            <Select.Trigger />
                            <Select.Content>
                              {DURATION_UNITS.map((unit) => (
                                <Select.Item key={unit} value={unit}>
                                  {unit}
                                </Select.Item>
                              ))}
                            </Select.Content>
                          </Select.Root>
                        </Flex>
                      </Flex>
                      {canWrite ? (
                        <IconButton
                          variant="soft"
                          color="red"
                          aria-label="Remove rule"
                          onClick={() =>
                            updateInvalidRules(invalidRules.filter((_, i) => i !== index))
                          }>
                          <TrashIcon />
                        </IconButton>
                      ) : null}
                    </Flex>
                  ))}
                </Flex>
              </Card>

              <Card>
                <Flex direction="column" gap="3">
                  <Flex justify="between" align="center">
                    <Heading as="h2" size="3">
                      Penalty Tiers
                    </Heading>
                    {canWrite ? (
                      <Button
                        variant="soft"
                        onClick={() =>
                          updateTiers([
                            ...tiers,
                            {
                              key: uuidv4(),
                              lockout: defaultDuration(),
                              probation: defaultDuration(),
                            },
                          ])
                        }>
                        Add tier
                      </Button>
                    ) : null}
                  </Flex>
                  <Text size="2" color="gray">
                    Escalation ladder applied on each breach. Tier 1 applies to a first offense; a
                    breach during a probation window escalates to the next tier.
                  </Text>
                  {tiers.length === 0 ? (
                    <Text size="2" color="red">
                      At least one penalty tier is required.
                    </Text>
                  ) : null}
                  {tiers.map((tier, index) => (
                    <Flex key={tier.key} gap="4" align="center" wrap="wrap">
                      <Text size="2" weight="medium" style={{ width: 50 }}>
                        Tier {index + 1}
                      </Text>
                      <Flex direction="column" gap="1">
                        <Text size="2" weight="medium">
                          Lockout
                        </Text>
                        {durationFields(tier.lockout, (updates) =>
                          updateTier(index, { lockout: { ...tier.lockout, ...updates } }),
                        )}
                      </Flex>
                      <Flex direction="column" gap="1">
                        <Text size="2" weight="medium">
                          Probation
                        </Text>
                        {durationFields(tier.probation, (updates) =>
                          updateTier(index, { probation: { ...tier.probation, ...updates } }),
                        )}
                      </Flex>
                      {canWrite ? (
                        <IconButton
                          variant="soft"
                          color="red"
                          aria-label="Remove tier"
                          onClick={() => updateTiers(tiers.filter((_, i) => i !== index))}>
                          <TrashIcon />
                        </IconButton>
                      ) : null}
                    </Flex>
                  ))}
                </Flex>
              </Card>

              <Card>
                <Flex direction="column" gap="3">
                  <Heading as="h2" size="3">
                    Alert Emails
                  </Heading>
                  <Text size="2" color="gray">
                    Comma-separated list of addresses notified when a player is blocked.
                  </Text>
                  <TextField.Root
                    value={alertEmails}
                    disabled={!canWrite}
                    placeholder="fraud-alerts@example.com, security@example.com"
                    onChange={(e) => updateAlertEmails(e.target.value)}
                  />
                </Flex>
              </Card>

              {submitError ? <Text color="red">{submitError}</Text> : null}
              {saved ? <Text color="green">Fraud config saved.</Text> : null}
              {canWrite ? (
                <Flex>
                  <Button onClick={() => saveConfig()} disabled={isSaving} loading={isSaving}>
                    Save
                  </Button>
                </Flex>
              ) : null}
            </Flex>
          </Skeleton>
        </Flex>
      </Section>
    </Container>
  )
}
